// Login screen. Email + password, with a TOTP field revealed when the server responds
// `mfa_required`. Gates the whole app. The session is a secure httpOnly cookie the server
// sets on success — this form never stores a credential.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { login, resetAuthError } from '../store/authStore';
import { useAuth } from '../store/hooks';
import { C, MONO } from '../lib/tokens';

export function LoginScreen() {
  const submitting = useAuth((s) => s.submitting);
  const error = useAuth((s) => s.error);
  const mfaRequired = useAuth((s) => s.mfaRequired);
  const expired = useAuth((s) => s.expired);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const emailRef = useRef<HTMLInputElement>(null);
  const totpRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);
  useEffect(() => {
    if (mfaRequired) totpRef.current?.focus();
  }, [mfaRequired]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    await login(email.trim(), password, mfaRequired ? totp.trim() : totp.trim() || undefined);
  }

  const field: React.CSSProperties = {
    height: 38,
    background: '#0A0F19',
    border: '1px solid ' + C.ln,
    borderRadius: 6,
    color: C.tx,
    fontSize: 13,
    padding: '0 12px',
    width: '100%',
    fontFamily: 'inherit',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    color: C.dm,
    fontWeight: 600,
    letterSpacing: '.04em',
    textTransform: 'uppercase',
    marginBottom: 5,
    display: 'block',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: C.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          width: 360,
          maxWidth: '94vw',
          background: '#111A2B',
          border: '1px solid ' + C.ln,
          borderRadius: 12,
          padding: '28px 28px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          boxShadow: '0 24px 60px rgba(0,0,0,.5)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontFamily: MONO, fontSize: 13, letterSpacing: '.28em', fontWeight: 600 }}>WATCHTOWER</div>
          <div style={{ fontSize: 10.5, color: C.dm, letterSpacing: '.08em' }}>MT5 FLEET MONITOR</div>
        </div>

        {expired && (
          <div
            role="status"
            style={{
              fontSize: 11.5,
              color: C.am,
              background: 'rgba(224,168,62,.1)',
              border: '1px solid rgba(224,168,62,.35)',
              borderRadius: 6,
              padding: '8px 10px',
              lineHeight: 1.5,
            }}
          >
            Your monitoring session expired. Sign in again to resume the live feed.
          </div>
        )}

        <div>
          <label style={labelStyle} htmlFor="wt-email">
            Email
          </label>
          <input
            id="wt-email"
            ref={emailRef}
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) resetAuthError();
            }}
            required
            style={field}
          />
        </div>

        <div>
          <label style={labelStyle} htmlFor="wt-pw">
            Password
          </label>
          <input
            id="wt-pw"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) resetAuthError();
            }}
            required
            style={field}
          />
        </div>

        {mfaRequired && (
          <div>
            <label style={labelStyle} htmlFor="wt-totp">
              Authenticator code
            </label>
            <input
              id="wt-totp"
              ref={totpRef}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={8}
              value={totp}
              onChange={(e) => {
                setTotp(e.target.value.replace(/[^0-9]/g, ''));
                if (error) resetAuthError();
              }}
              placeholder="123456"
              style={{ ...field, fontFamily: MONO, letterSpacing: '.3em' }}
            />
          </div>
        )}

        {error && (
          <div role="alert" style={{ fontSize: 11.5, color: C.rd, lineHeight: 1.5 }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          style={{
            height: 40,
            background: C.bl,
            border: 'none',
            borderRadius: 6,
            color: '#0B1220',
            fontWeight: 600,
            fontSize: 13,
            fontFamily: 'inherit',
            opacity: submitting ? 0.7 : 1,
            cursor: submitting ? 'default' : 'pointer',
          }}
        >
          {submitting ? 'Signing in…' : mfaRequired ? 'Verify & sign in' : 'Sign in'}
        </button>

        <div style={{ fontSize: 10, color: C.dim2, textAlign: 'center', lineHeight: 1.5 }}>
          Read-only monitoring console. No trading actions are possible from this interface.
        </div>
      </form>
    </div>
  );
}
